/**
 * BodyScreen.jsx
 * Body/weight tracking with REAL data from backend
 * Now includes InBody integration (when enabled)
 */
import React, { useState, useEffect } from 'react';
import { Plus, TrendingDown, TrendingUp, Minus, RefreshCw, Scale, Ruler, Activity, Scan, ChevronRight, Droplets, Dumbbell, Heart, Percent, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BodyMeasurementsTab from './BodyMeasurementsTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return `${days[date.getDay()]}, ${date.getDate()}.${date.getMonth() + 1}.`;
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getDate()}.${date.getMonth() + 1}.`;
};

// Get trend icon
const getTrendIcon = (direction) => {
  switch (direction) {
    case 'down':
      return <TrendingDown className="w-4 h-4 text-green-400" />;
    case 'up':
      return <TrendingUp className="w-4 h-4 text-red-400" />;
    default:
      return <Minus className="w-4 h-4 text-zinc-400" />;
  }
};

export const BodyScreen = ({ 
  bodyData = { entries: [], latest: null, progress: null },
  onAddEntry,
  onRefresh,
  loading = false,
  portalToken
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('weight');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newWeight, setNewWeight] = useState('');
  const [newHeight, setNewHeight] = useState('');
  const [newBodyFat, setNewBodyFat] = useState('');
  const [saving, setSaving] = useState(false);
  
  // InBody state
  const [inbodyVisible, setInbodyVisible] = useState(false);
  const [inbodyMeasurements, setInbodyMeasurements] = useState([]);
  const [inbodyLoading, setInbodyLoading] = useState(false);
  const [selectedInbody, setSelectedInbody] = useState(null);
  const [showInbodyDetail, setShowInbodyDetail] = useState(false);

  const { entries, latest, progress } = bodyData;

  // Check if InBody is enabled
  useEffect(() => {
    const checkInbodyVisibility = async () => {
      try {
        const response = await axios.get(`${API}/inbody/portal-settings`);
        setInbodyVisible(response.data.inbody_visible || false);
      } catch (error) {
        setInbodyVisible(false);
      }
    };
    checkInbodyVisibility();
  }, []);

  // Load InBody measurements when tab is active
  useEffect(() => {
    if (activeTab === 'inbody' && inbodyVisible && portalToken) {
      loadInbodyMeasurements();
    }
  }, [activeTab, inbodyVisible, portalToken]);

  const loadInbodyMeasurements = async () => {
    if (!portalToken) return;
    setInbodyLoading(true);
    try {
      const response = await axios.get(`${API}/inbody/portal/measurements`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setInbodyMeasurements(response.data.measurements || []);
      if (response.data.measurements?.length > 0) {
        setSelectedInbody(response.data.measurements[0]);
      }
    } catch (error) {
      console.error('Error loading InBody:', error);
      // Silent fail - no toast for 404/401
    } finally {
      setInbodyLoading(false);
    }
  };

  // Handle add entry
  const handleAddEntry = async () => {
    if (!newWeight) {
      toast.error(t('body.enter_weight'));
      return;
    }

    setSaving(true);
    try {
      if (onAddEntry) {
        await onAddEntry({
          weight: parseFloat(newWeight),
          height: newHeight ? parseFloat(newHeight) : undefined,
          body_fat_percent: newBodyFat ? parseFloat(newBodyFat) : undefined
        });
        toast.success(t('body.saved'));
        setShowAddModal(false);
        setNewWeight('');
        setNewBodyFat('');
      }
    } catch (error) {
      toast.error(t('body.save_error'));
    } finally {
      setSaving(false);
    }
  };

  // Get weight change
  const weightChange = progress?.changes?.weight;
  const bmiChange = progress?.changes?.bmi;

  return (
    <div className="p-4 space-y-3">
      <div className="flex justify-between items-center pt-2">
        <h1 className="text-white text-base font-bold">{t('body.title')}</h1>
        {onRefresh && (
          <button 
            onClick={onRefresh}
            disabled={loading}
            className="p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700"
          >
            <RefreshCw className={`w-4 h-4 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button 
          onClick={() => setActiveTab('weight')}
          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
            activeTab === 'weight' ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-300'
          }`}
        >
          Gewicht
        </button>
        <button 
          onClick={() => setActiveTab('measurements')}
          className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
            activeTab === 'measurements' ? 'bg-yellow-500 text-black font-medium' : 'bg-zinc-800 text-zinc-300'
          }`}
        >
          Masse
        </button>
        {inbodyVisible && (
          <button 
            onClick={() => setActiveTab('inbody')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'inbody' ? 'bg-yellow-500 text-black font-medium' : 'bg-zinc-800 text-zinc-300'
            }`}
            data-testid="inbody-tab"
          >
            <Scan className="w-3.5 h-3.5" />
            InBody
          </button>
        )}
        <button 
          onClick={() => setActiveTab('photos')}
          className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
            activeTab === 'photos' ? 'bg-yellow-500 text-black font-medium' : 'bg-zinc-800 text-zinc-300'
          }`}
        >
          Fotos
        </button>
      </div>

      {/* Masse Tab Content — Massband (cm) + Kaliper (mm) inkl. eigener
          Messstellen & Körperfett-Berechnung (Parität zur Mobile-App) */}
      {activeTab === 'measurements' && (
        <BodyMeasurementsTab portalToken={portalToken} />
      )}

      {/* InBody Tab Content */}
      {activeTab === 'inbody' && inbodyVisible && (
        <div className="space-y-3">
          {inbodyLoading ? (
            <div className="bg-zinc-800/50 rounded-xl p-8 text-center">
              <RefreshCw className="w-6 h-6 text-yellow-500 animate-spin mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">{t('body.inbody_loading')}</p>
            </div>
          ) : inbodyMeasurements.length === 0 ? (
            <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
              <Scan className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-white font-medium mb-1">{t('body.inbody_no_data')}</p>
              <p className="text-zinc-400 text-sm">
                Fragen Sie Ihren Trainer nach einer Körperanalyse mit dem InBody-Gerät.
              </p>
            </div>
          ) : (
            <>
              {/* Latest InBody Measurement */}
              {selectedInbody && (
                <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/10 rounded-2xl p-5 border border-blue-500/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Scan className="w-5 h-5 text-blue-400" />
                      <span className="text-white font-medium">{t('body.inbody_analysis')}</span>
                    </div>
                    <span className="text-zinc-400 text-xs">
                      {formatDate(selectedInbody.measured_at)}
                    </span>
                  </div>
                  
                  {/* Main Metrics Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Weight */}
                    <div className="bg-zinc-900/50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Scale className="w-4 h-4 text-yellow-400" />
                        <span className="text-zinc-400 text-xs">{t('body.weight')}</span>
                      </div>
                      <p className="text-white text-xl font-bold">{selectedInbody.weight || '--'} kg</p>
                    </div>
                    
                    {/* Muscle Mass */}
                    <div className="bg-zinc-900/50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Dumbbell className="w-4 h-4 text-red-400" />
                        <span className="text-zinc-400 text-xs">{t('body.muscle_mass')}</span>
                      </div>
                      <p className="text-white text-xl font-bold">{selectedInbody.smm || selectedInbody.muscle_mass || '--'} kg</p>
                    </div>
                    
                    {/* Body Fat */}
                    <div className="bg-zinc-900/50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Percent className="w-4 h-4 text-orange-400" />
                        <span className="text-zinc-400 text-xs">{t('body.body_fat')}</span>
                      </div>
                      <p className="text-white text-xl font-bold">{selectedInbody.pbf || selectedInbody.body_fat_percent || '--'}%</p>
                    </div>
                    
                    {/* Body Water */}
                    <div className="bg-zinc-900/50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Droplets className="w-4 h-4 text-blue-400" />
                        <span className="text-zinc-400 text-xs">{t('body.water')}</span>
                      </div>
                      <p className="text-white text-xl font-bold">{selectedInbody.tbw || '--'} L</p>
                    </div>
                  </div>
                  
                  {/* Additional Metrics */}
                  <div className="mt-3 pt-3 border-t border-zinc-700/50">
                    <div className="flex justify-between text-sm">
                      <div className="text-center">
                        <p className="text-zinc-500 text-xs">BMI</p>
                        <p className="text-white font-medium">{selectedInbody.bmi || '--'}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-zinc-500 text-xs">{t('body.visceral_fat')}</p>
                        <p className="text-white font-medium">{selectedInbody.vfl || '--'}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-zinc-500 text-xs">{t('body.basal_rate')}</p>
                        <p className="text-white font-medium">{selectedInbody.bmr || '--'} kcal</p>
                      </div>
                      <div className="text-center">
                        <p className="text-zinc-500 text-xs">{t('body.inbody_score')}</p>
                        <p className="text-white font-medium">{selectedInbody.inbody_score || '--'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* View Details Button */}
                  <button 
                    onClick={() => setShowInbodyDetail(true)}
                    className="w-full mt-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                  >
                    Alle Details anzeigen
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* History */}
              {inbodyMeasurements.length > 1 && (
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h2 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-yellow-400" />
                    Messungsverlauf
                  </h2>
                  {inbodyMeasurements.slice(0, 5).map((m, i) => (
                    <button
                      key={m.id || i}
                      onClick={() => setSelectedInbody(m)}
                      className={`w-full flex items-center justify-between py-2.5 px-3 rounded-lg mb-1 ${
                        selectedInbody?.id === m.id ? 'bg-yellow-500/20' : 'hover:bg-zinc-700/50'
                      }`}
                    >
                      <span className="text-zinc-400 text-sm">{formatDate(m.measured_at)}</span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-white">{m.weight} kg</span>
                        <span className="text-zinc-500">{m.pbf || m.body_fat_percent}%</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* InBody Detail Modal */}
      {showInbodyDetail && selectedInbody && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
          <div className="w-full bg-zinc-900 rounded-t-3xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Scan className="w-5 h-5 text-blue-400" />
                InBody Details
              </h3>
              <button 
                onClick={() => setShowInbodyDetail(false)}
                className="text-zinc-400 text-lg p-1"
              >
                ✕
              </button>
            </div>
            
            <p className="text-zinc-400 text-sm mb-4">
              Messung vom {formatDate(selectedInbody.measured_at)}
            </p>
            
            <div className="space-y-3">
              {/* Body Composition */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <h4 className="text-yellow-400 text-sm font-medium mb-3">{t('body.body_composition')}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">{t('body.weight')}</span>
                    <span className="text-white font-medium">{selectedInbody.weight} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">{t('body.smm')}</span>
                    <span className="text-white font-medium">{selectedInbody.smm || selectedInbody.muscle_mass || '--'} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">{t('body.bfm')}</span>
                    <span className="text-white font-medium">{selectedInbody.bfm || '--'} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">{t('body.bfp')}</span>
                    <span className="text-white font-medium">{selectedInbody.pbf || selectedInbody.body_fat_percent || '--'}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">{t('body.water')}</span>
                    <span className="text-white font-medium">{selectedInbody.tbw || '--'} L</span>
                  </div>
                </div>
              </div>
              
              {/* Health Indicators */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <h4 className="text-yellow-400 text-sm font-medium mb-3">{t('body.health_indicators')}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">BMI</span>
                    <span className="text-white font-medium">{selectedInbody.bmi || '--'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Viszeralfett-Level</span>
                    <span className="text-white font-medium">{selectedInbody.vfl || '--'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Grundumsatz (BMR)</span>
                    <span className="text-white font-medium">{selectedInbody.bmr || '--'} kcal</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">{t('body.inbody_score')}</span>
                    <span className="text-white font-medium">{selectedInbody.inbody_score || '--'}</span>
                  </div>
                </div>
              </div>
              
              {/* Segmental Analysis if available */}
              {(selectedInbody.lean_arm_left || selectedInbody.lean_arm_right) && (
                <div className="bg-zinc-800 rounded-xl p-4">
                  <h4 className="text-yellow-400 text-sm font-medium mb-3">{t('body.segmental_analysis')}</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-zinc-500 text-xs">Linker Arm</p>
                      <p className="text-white">{selectedInbody.lean_arm_left || '--'} kg</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs">Rechter Arm</p>
                      <p className="text-white">{selectedInbody.lean_arm_right || '--'} kg</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs">Linkes Bein</p>
                      <p className="text-white">{selectedInbody.lean_leg_left || '--'} kg</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs">Rechtes Bein</p>
                      <p className="text-white">{selectedInbody.lean_leg_right || '--'} kg</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-zinc-500 text-xs">Rumpf</p>
                      <p className="text-white">{selectedInbody.lean_trunk || '--'} kg</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Info Note */}
              <div className="bg-blue-500/10 rounded-xl p-4 flex gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-zinc-400 text-xs">
                  Die InBody-Analyse wird bei Ihrem Trainer im Studio durchgeführt. 
                  Für optimale Ergebnisse kommen Sie nüchtern und gut hydriert.
                </p>
              </div>
            </div>
            
            <button 
              onClick={() => setShowInbodyDetail(false)}
              className="w-full mt-4 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-medium"
            >
              Schliessen
            </button>
          </div>
        </div>
      )}

      {/* Weight Tab Content - only show when weight tab is active */}
      {activeTab === 'weight' && (
        <>
      <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-2xl p-5 border border-yellow-500/30">
        <div className="flex flex-col items-center text-center">
          <p className="text-zinc-400 text-sm">{t('body.current_weight')}</p>
          <p className="text-white text-4xl font-bold mt-1">
            {latest?.weight ? `${latest.weight} kg` : '-- kg'}
          </p>
          {weightChange && (
            <div className="flex items-center gap-2 mt-2">
              {getTrendIcon(weightChange.direction)}
              <span className={`text-sm ${
                weightChange.direction === 'down' ? 'text-green-400' : 
                weightChange.direction === 'up' ? 'text-red-400' : 'text-zinc-400'
              }`}>
                {weightChange.change > 0 ? '+' : ''}{weightChange.change} kg
              </span>
              <span className="text-zinc-500 text-sm">seit Start</span>
            </div>
          )}
        </div>
        
        {/* BMI and Body Fat */}
        {(latest?.bmi || latest?.body_fat_percent) && (
          <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-yellow-500/20">
            {latest?.bmi && (
              <div className="text-center">
                <p className="text-zinc-400 text-xs">BMI</p>
                <p className="text-white font-bold">{latest.bmi}</p>
              </div>
            )}
            {latest?.body_fat_percent && (
              <div className="text-center">
                <p className="text-zinc-400 text-xs">{t('body.body_fat')}</p>
                <p className="text-white font-bold">{latest.body_fat_percent}%</p>
              </div>
            )}
            {latest?.muscle_mass && (
              <div className="text-center">
                <p className="text-zinc-400 text-xs">{t('body.muscle_mass')}</p>
                <p className="text-white font-bold">{latest.muscle_mass} kg</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Weight Chart */}
      {entries.length > 0 && (
        <div className="bg-zinc-800/50 rounded-xl p-4">
          <div className="flex justify-between items-center mb-3">
            <span className="text-white font-medium">Verlauf</span>
            <span className="text-zinc-400 text-sm">{entries.length} Einträge</span>
          </div>
          <div className="h-32 flex items-end justify-between gap-2">
            {entries.slice(0, 7).reverse().map((entry, i) => {
              // Calculate bar height based on weight range
              const weights = entries.map(e => e.weight).filter(Boolean);
              const minWeight = Math.min(...weights) - 2;
              const maxWeight = Math.max(...weights) + 2;
              const range = maxWeight - minWeight || 1;
              const height = ((entry.weight - minWeight) / range) * 100;
              
              return (
                <div key={entry.id || i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-zinc-400 text-[10px]">{entry.weight}</span>
                  <div 
                    className="w-full bg-yellow-500 rounded-t transition-all" 
                    style={{ height: `${Math.max(height, 10)}%` }}
                  />
                  <span className="text-zinc-500 text-[10px]">{formatShortDate(entry.date)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Progress Summary */}
      {progress?.has_data && (
        <div className="bg-zinc-800/50 rounded-xl p-4">
          <h2 className="text-white font-medium mb-3">Fortschritt</h2>
          <div className="grid grid-cols-2 gap-3">
            {progress.changes.weight && (
              <div className="bg-zinc-900/50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-yellow-400" />
                  <span className="text-zinc-400 text-xs">{t('body.weight')}</span>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  {getTrendIcon(progress.changes.weight.direction)}
                  <span className={`font-bold ${
                    progress.changes.weight.direction === 'down' ? 'text-green-400' : 
                    progress.changes.weight.direction === 'up' ? 'text-red-400' : 'text-white'
                  }`}>
                    {progress.changes.weight.change > 0 ? '+' : ''}{progress.changes.weight.change} kg
                  </span>
                </div>
              </div>
            )}
            {progress.changes.bmi && (
              <div className="bg-zinc-900/50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-yellow-400" />
                  <span className="text-zinc-400 text-xs">BMI</span>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  {getTrendIcon(progress.changes.bmi.direction)}
                  <span className={`font-bold ${
                    progress.changes.bmi.direction === 'down' ? 'text-green-400' : 
                    progress.changes.bmi.direction === 'up' ? 'text-red-400' : 'text-white'
                  }`}>
                    {progress.changes.bmi.change > 0 ? '+' : ''}{progress.changes.bmi.change}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Entries */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <h2 className="text-white font-medium mb-3">{t('body.recent_entries')}</h2>
        {entries.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-4">Keine Einträge vorhanden</p>
        ) : (
          entries.slice(0, 5).map((entry, i) => (
            <div key={entry.id || i} className="flex items-center justify-between py-2 border-b border-zinc-700/50 last:border-0">
              <span className="text-zinc-400 text-sm">{formatDate(entry.date)}</span>
              <div className="flex items-center gap-3">
                {entry.body_fat_percent && (
                  <span className="text-zinc-500 text-xs">{entry.body_fat_percent}%</span>
                )}
                <span className="text-white font-medium">{entry.weight} kg</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Button */}
      <button 
        onClick={() => setShowAddModal(true)}
        className="w-full py-3 bg-yellow-500 text-black rounded-xl font-medium flex items-center justify-center gap-2"
      >
        <Plus className="w-5 h-5" />
        {t('body.new_entry')}
      </button>
      </>
      )}

      {/* Add Entry Modal - always available */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-white font-bold text-lg mb-4">{t('body.new_entry')}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-zinc-400 text-sm block mb-1">{t('body.weight_kg')}</label>
                <input
                  type="number"
                  step="0.1"
                  value={newWeight}
                  onChange={(e) => setNewWeight(e.target.value)}
                  placeholder="z.B. 78.5"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                />
              </div>
              
              <div>
                <label className="text-zinc-400 text-sm block mb-1">{t('body.body_fat_pct')}</label>
                <input
                  type="number"
                  step="0.1"
                  value={newBodyFat}
                  onChange={(e) => setNewBodyFat(e.target.value)}
                  placeholder="z.B. 18.0"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                />
              </div>

              {!latest?.height && (
                <div>
                  <label className="text-zinc-400 text-sm block mb-1">{t('body.height_cm')}</label>
                  <input
                    type="number"
                    value={newHeight}
                    onChange={(e) => setNewHeight(e.target.value)}
                    placeholder="z.B. 178"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <button 
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-3 bg-zinc-700 rounded-xl text-zinc-300"
              >
                Abbrechen
              </button>
              <button 
                onClick={handleAddEntry}
                disabled={saving || !newWeight}
                className="flex-1 py-3 bg-yellow-500 rounded-xl text-black font-medium disabled:opacity-50"
              >
                {saving ? t('body.saving') : t('body.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BodyScreen;
